// Folder: frontend/src/app-lib/
// File: api-client.ts
// Fix #1 - 2025-01-15 13:30:00Z: Enhanced error handling and timeout management

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import toast from 'react-hot-toast';

interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

interface VideoConfiguration {
  enhanced_video_config: {
    quality_settings: {
      resolution: string;
      frame_rate: string;
      bitrate: string;
      codec: string;
    };
    audio_enhancement: {
      noise_reduction: boolean;
      volume_normalization: boolean;
      background_music: {
        enabled: boolean;
        volume: number;
        fade_during_speech: boolean;
      };
    };
  };
  content_templates: {
    title_templates: Template[];
    description_templates: Template[];
    tag_sets: Template[];
  };
  legacy_fields: {
    intro_clip?: string;
    outro_clip?: string;
    title_font_size: number;
    subtitle_font_size: number;
    position_choice: string;
    group_position: string;
  };
}

interface Template {
  template_id: string;
  name: string;
  template: string;
  variables: string[];
  tags?: string[];
  category: string;
  usage_count: number;
  performance_score: number;
}

class ApiClient {
  private client: AxiosInstance;
  private authToken: string | null = null;
  private isServerDown: boolean = false;
  private retryAttempts: number = 0;
  private maxRetries: number = 3;

  constructor(baseURL: string) {
    // PERFORMANCE FIX - 2025-01-09: Optimized timeout values
    this.client = axios.create({
      baseURL,
      timeout: 30000, // Increased to 30s for better reliability with optimized backend
      headers: {
        'Content-Type': 'application/json',
      },
    });

    
    this.setupInterceptors();


  }
 
  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
          console.log(`🔑 Auth token set for ${config.url}`);
        }
        else
        {
          console.warn(`🔑 No auth token set for ${config.url}`);
        }

        // Auto-increase timeout for analytics endpoints if not already set
        if (config.url?.includes('/analytics/') && !config.timeout) {
          config.timeout = 60000; // 60 seconds for analytics
          if (import.meta.env.DEV) {
            console.log(`⏱️ Extended timeout to 60s for analytics request: ${config.url}`);
          }
        }

        // Log requests in development
        if (import.meta.env.DEV) {
          console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        }

        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Reset server down flag and retry attempts on successful response
        if (this.isServerDown) {
          this.isServerDown = false;
          this.retryAttempts = 0;
          console.log('✅ Server connection restored');
        }
        
        // Log responses in development
        if (import.meta.env.DEV) {
          console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        }
        
        return response;
      },
      (error: AxiosError) => {
        this.handleResponseError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleResponseError(error: AxiosError): void {
    const { response, message, code } = error;
    const url = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'GET';

    // Log error details
    console.error(`❌ API Error: ${response?.status || 'Network'} ${url} ${message}`);

    // Handle different error types
    if (code === 'ECONNABORTED' || message.includes('timeout')) {
      this.handleTimeoutError(url, method);
    } else if (!response) {
      this.handleNetworkError(url, method);
    } else {
      this.handleHttpError(response, url, method);
    }
  }

  private handleTimeoutError(url: string, method: string): void {
    console.warn(`⏰ Timeout error: ${method} ${url}`);
    
    if (!this.isServerDown) {
      this.isServerDown = true;
      toast.error('Server is responding slowly. Please check your connection.');
    }
  }

  private handleNetworkError(url: string, method: string): void {
    console.warn(`🌐 Network error: ${method} ${url}`);
    
    if (!this.isServerDown) {
      this.isServerDown = true;
      toast.error('Cannot connect to server. Please check if the backend is running.');
    }
  }

  private handleHttpError(response: AxiosResponse, url: string, method: string): void {
    const { status, data } = response;
    
    switch (status) {
      case 401:
        // Unauthorized - try to refresh token first
        console.warn('🔑 401 Unauthorized - attempting token refresh');
        this.clearToken();
        localStorage.removeItem('auth_token');
        
        // Clear token and redirect
        this.clearToken();
        localStorage.removeItem('auth_token');
        
        // If refresh failed, redirect to login
        if (!window.location.pathname.startsWith('/auth')) {
          toast.error('Session expired. Please sign in again.');
          window.location.href = '/auth/signin';
        }
        break;
        
      case 403:
        toast.error('Access denied. Please check your permissions.');
        break;
        
      case 404:
        // Don't show toast for 404s as they might be expected
        console.warn(`📭 Not found: ${method} ${url}`);
        break;
        
      case 429:
        toast.error('Too many requests. Please wait a moment.');
        break;
        
      case 500:
      case 502:
      case 503:
      case 504:
        if (!this.isServerDown) {
          this.isServerDown = true;
          toast.error('Server error. Please try again later.');
        }
        break;
        
      default:
        const errorMessage = data?.detail || data?.message || `Request failed with status ${status}`;
        toast.error(errorMessage);
    }
  }

  // Enhanced retry mechanism
  private async retryRequest<T>(
    requestFn: () => Promise<AxiosResponse<T>>,
    retries: number = this.maxRetries
  ): Promise<AxiosResponse<T>> {
    try {
      return await requestFn();
    } catch (error: any) {
      if (retries > 0 && this.shouldRetry(error)) {
        console.log(`🔄 Retrying request... (${this.maxRetries - retries + 1}/${this.maxRetries})`);
        await this.delay(1000 * (this.maxRetries - retries + 1)); // Exponential backoff
        return this.retryRequest(requestFn, retries - 1);
      }
      throw error;
    }
  }

  private shouldRetry(error: any): boolean {
    // Retry on network errors, timeouts, and 5xx errors
    return (
      !error.response || 
      error.code === 'ECONNABORTED' ||
      (error.response.status >= 500 && error.response.status < 600)
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Public methods
  setToken(token: string): void {
    this.authToken = token;
    if (import.meta.env.DEV) {
      console.log('🔑 API Token set');
    }
  }

  clearToken(): void {
    this.authToken = null;
    if (import.meta.env.DEV) {
      console.log('🔓 API Token cleared');
    }
  }

  getToken(): string | null {
    return this.authToken;
  }

  // HTTP Methods with retry logic
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retryRequest(() => this.client.get<T>(url, config));
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retryRequest(() => this.client.post<T>(url, data, config));
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retryRequest(() => this.client.put<T>(url, data, config));
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retryRequest(() => this.client.patch<T>(url, data, config));
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.retryRequest(() => this.client.delete<T>(url, config));
  }

  // Upload file with progress
  async uploadFile<T = any>(
    url: string, 
    file: File, 
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      timeout: 60000, // Longer timeout for uploads
    });
  }
  
  // YouTube auth helper (with better error handling)
  async initYouTubeAuth(channelId: string, redirectUrl?: string): Promise<{ url: string; channel_id: string; redirect_url?: string }> {
    try {
      const params = new URLSearchParams({ channel_id: channelId });
      if (redirectUrl) {
        params.append('redirect_url', redirectUrl);
      }
      
      const response = await this.get<{ url: string; channel_id: string; redirect_url?: string }>
        (`/api/auth/youtube/init?${params.toString()}`);
      
      return response.data;
    } catch (error: any) {
      console.error('YouTube auth init failed:', error);
      if (error.code === 'ECONNABORTED') {
        throw new Error('YouTube authentication service is not responding. Please try again later.');
      }
      throw error;
    }
  }

  // Health check with better error handling
  async healthCheck(): Promise<boolean> {
    try {
      await this.client.get('/health', { timeout: 5000 });
      return true;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  // Check if server is reachable
  async checkServerStatus(): Promise<{ isReachable: boolean; latency?: number }> {
    const startTime = Date.now();
    try {
      await this.healthCheck();
      const latency = Date.now() - startTime;
      return { isReachable: true, latency };
    } catch (error) {
      return { isReachable: false };
    }
  }

  // Update base URL
  updateBaseURL(baseURL: string): void {
    this.client.defaults.baseURL = baseURL;
    this.isServerDown = false; // Reset server status
    this.retryAttempts = 0;
    if (import.meta.env.DEV) {
      console.log('🔄 API Base URL updated:', baseURL);
    }
  }

  getBaseURL(): string | undefined {
    return this.client.defaults.baseURL;
  }

  // Get server status
  getServerStatus(): { isDown: boolean; retryAttempts: number } {
    return {
      isDown: this.isServerDown,
      retryAttempts: this.retryAttempts
    };
  }

    // Video Configuration Methods - ADD THESE
  async getVideoConfiguration(channelId: string): Promise<VideoConfiguration> {
    const response = await this.client.get(`/api/video-configuration/channels/${channelId}/config`);
    return response.data.configuration;
  }

  async updateVideoConfiguration(channelId: string, config: VideoConfiguration): Promise<void> {
    await this.client.put(`/api/video-configuration/channels/${channelId}/config`, config);
  }

  async getVideoProcessingSettings(channelId: string): Promise<any> {
    const response = await this.client.get(`/api/video-configuration/channels/${channelId}/processing-settings`);
    return response.data.settings;
  }

  // Template Methods - ADD THESE
  async getTemplates(channelId: string, templateType?: string): Promise<Template[]> {
    const params = templateType ? { template_type: templateType } : {};
    const response = await this.client.get(`/api/video-configuration/channels/${channelId}/templates`, { params });
    return response.data.templates;
  }

  async createTemplate(channelId: string, templateData: Partial<Template>): Promise<string> {
    const response = await this.client.post(`/api/video-configuration/channels/${channelId}/templates`, templateData);
    return response.data.template_id;
  }

  async applyTitleTemplate(channelId: string, templateId: string, variables: Record<string, string>): Promise<string> {
    const response = await this.client.post(
      `/api/video-configuration/channels/${channelId}/templates/${templateId}/apply-title`,
      variables
    );
    return response.data.generated_title;
  }

  async applyDescriptionTemplate(channelId: string, templateId: string, variables: Record<string, string>): Promise<string> {
    const response = await this.client.post(
      `/api/video-configuration/channels/${channelId}/templates/${templateId}/apply-description`,
      variables
    );
    return response.data.generated_description;
  }

  async getTagSet(channelId: string, templateId: string): Promise<string[]> {
    const response = await this.client.get(`/api/video-configuration/channels/${channelId}/templates/${templateId}/tags`);
    return response.data.tags;
  }

  // Preset Methods - ADD THESE
  async applyPreset(channelId: string, presetName: string): Promise<void> {
    await this.client.post(`/api/video-configuration/channels/${channelId}/apply-preset?preset_name=${presetName}`);
  }

  async getPresets(): Promise<any[]> {
    const response = await this.client.get('/api/video-configuration/presets');
    return response.data.presets;
  }
}

class AuthenticatedApiClient extends ApiClient {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async get<T = any>(url: string, config?: any) {
    const authConfig = {
      ...config,
      headers: {
        ...this.getAuthHeaders(),
        ...config?.headers
      }
    };
    return super.get<T>(url, authConfig);
  }

  async post<T = any>(url: string, data?: any, config?: any) {
    const authConfig = {
      ...config,
      headers: {
        ...this.getAuthHeaders(),
        ...config?.headers
      }
    };
    return super.post<T>(url, data, authConfig);
  }

  async put<T = any>(url: string, data?: any, config?: any) {
    const authConfig = {
      ...config,
      headers: {
        ...this.getAuthHeaders(),
        ...config?.headers
      }
    };
    return super.put<T>(url, data, authConfig);
  }

  async patch<T = any>(url: string, data?: any, config?: any) {
    const authConfig = {
      ...config,
      headers: {
        ...this.getAuthHeaders(),
        ...config?.headers
      }
    };
    return super.patch<T>(url, data, authConfig);
  }

  async delete<T = any>(url: string, config?: any) {
    const authConfig = {
      ...config,
      headers: {
        ...this.getAuthHeaders(),
        ...config?.headers
      }
    };
    return super.delete<T>(url, config);
  }
}

export const apiClient = new AuthenticatedApiClient(
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005'
);

// Initialize token from storage on app start
const initializeToken = () => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    apiClient.setToken(token);
    console.log('🔑 API client initialized with stored token');
  }
};

// Call initialization
initializeToken();

export type { VideoConfiguration, Template };

// Export the class for testing or creating additional instances
export { ApiClient };

// Type exports for consumers
export type { ApiResponse };

// // File: frontend/src/lib/api-client.ts
// import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
// import toast from 'react-hot-toast';

// // Types
// interface ApiError {
//   message: string;
//   code?: string;
//   details?: any;
// }

// interface ApiResponse<T = any> {
//   data: T;
//   success: boolean;
//   message?: string;
//   error?: ApiError;
// }

// class ApiClient {
//   private client: AxiosInstance;
//   private baseURL: string;

//   constructor() {
//     this.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8005';
    
//     this.client = axios.create({
//       baseURL: this.baseURL,
//       timeout: 30000, // 30 seconds for analytics requests
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     });

//     this.setupInterceptors();
//   }

//   private setupInterceptors() {
//     // Request interceptor
//     this.client.interceptors.request.use(
//       (config) => {
//         // Add auth token if available
//         const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
//         if (token) {
//           config.headers.Authorization = `Bearer ${token}`;
//         }

//         // Add user context
//         const userId = localStorage.getItem('user_id');
//         if (userId) {
//           config.headers['X-User-Id'] = userId;
//         }

//         // Log request for debugging
//         if (import.meta.env.DEV) {
//           console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
//             params: config.params,
//             data: config.data
//           });
//         }

//         return config;
//       },
//       (error) => {
//         console.error('Request interceptor error:', error);
//         return Promise.reject(error);
//       }
//     );

//     // Response interceptor
//     this.client.interceptors.response.use(
//       (response: AxiosResponse) => {
//         // Log successful responses in dev
//         if (import.meta.env.DEV) {
//           console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, response.data);
//         }

//         return response;
//       },
//       (error) => {
//         this.handleResponseError(error);
//         return Promise.reject(error);
//       }
//     );
//   }

//   private handleResponseError(error: any) {
//     const { response, request, message } = error;

//     // Log error for debugging
//     console.error('API Error:', {
//       url: error.config?.url,
//       method: error.config?.method,
//       status: response?.status,
//       data: response?.data,
//       message: message
//     });

//     if (response) {
//       // Server responded with error status
//       const { status, data } = response;

//       switch (status) {
//         case 401:
//           // Unauthorized - clear tokens and redirect to login
//           localStorage.removeItem('access_token');
//           localStorage.removeItem('refresh_token');
//           sessionStorage.removeItem('access_token');
          
//           if (window.location.pathname !== '/auth/login') {
//             toast.error('Session expired. Please log in again.');
//             window.location.href = '/auth/login';
//           }
//           break;

//         case 403:
//           // Forbidden - insufficient permissions
//           toast.error('Access denied. Please upgrade your plan or contact support.');
//           break;

//         case 404:
//           // Not found
//           if (!error.config?.url?.includes('/analytics/')) {
//             toast.error('Resource not found.');
//           }
//           break;

//         case 429:
//           // Rate limited
//           toast.error('Too many requests. Please wait a moment and try again.');
//           break;

//         case 500:
//           // Server error
//           toast.error('Server error. Please try again later.');
//           break;

//         case 503:
//           // Service unavailable
//           toast.error('Service temporarily unavailable. Please try again later.');
//           break;

//         default:
//           // Other errors
//           const errorMessage = data?.detail || data?.message || 'An unexpected error occurred.';
//           if (!error.config?.skipErrorToast) {
//             toast.error(errorMessage);
//           }
//       }
//     } else if (request) {
//       // Network error
//       toast.error('Network error. Please check your connection and try again.');
//     } else {
//       // Request setup error
//       toast.error('Request failed. Please try again.');
//     }
//   }

//   // Generic request method
//   async request<T = any>(config: AxiosRequestConfig): Promise<T> {
//     try {
//       const response = await this.client.request<T>(config);
//       return response.data;
//     } catch (error) {
//       throw error;
//     }
//   }

//   // GET method
//   async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
//     return this.client.get<T>(url, config);
//   }

//   // POST method
//   async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
//     return this.client.post<T>(url, data, config);
//   }

//   // PUT method
//   async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
//     return this.client.put<T>(url, data, config);
//   }

//   // PATCH method
//   async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
//     return this.client.patch<T>(url, data, config);
//   }

//   // DELETE method
//   async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
//     return this.client.delete<T>(url, config);
//   }

//   // Analytics-specific methods
//   async getChannelAnalytics(channelId: string, params: {
//     start_date: string;
//     end_date: string;
//     metrics?: string;
//     dimensions?: string;
//   }) {
//     return this.get(`/api/analytics/channels/${channelId}`, { params });
//   }

//   async getChannelVideos(channelId: string, params: {
//     maxResults?: number;
//     order?: string;
//     publishedAfter?: string;
//   }) {
//     return this.get(`/api/analytics/channels/${channelId}/videos`, { params });
//   }

//   async getChannelDemographics(channelId: string, params: {
//     start_date: string;
//     end_date: string;
//   }) {
//     return this.get(`/api/analytics/channels/${channelId}/demographics`, { params });
//   }

//   async exportAnalyticsData(channelId: string, data: {
//     data: any;
//     format: 'csv' | 'xlsx' | 'pdf';
//     date_range: {
//       start: string;
//       end: string;
//     };
//   }) {
//     return this.post('/api/analytics/export', data, {
//       responseType: 'blob' // For file downloads
//     });
//   }

//   async generateAIInsights(channelId: string) {
//     return this.post(`/api/analytics/channels/${channelId}/generate-insights`);
//   }

//   async checkYouTubeAuth(channelId: string) {
//     return this.get(`/api/youtube/auth-status/${channelId}`);
//   }

//   // Retry mechanism for analytics requests
//   async retryRequest<T>(
//     requestFn: () => Promise<T>,
//     maxRetries: number = 3,
//     delay: number = 1000
//   ): Promise<T> {
//     let lastError: any;

//     for (let i = 0; i <= maxRetries; i++) {
//       try {
//         return await requestFn();
//       } catch (error: any) {
//         lastError = error;
        
//         // Don't retry on certain status codes
//         if (error.response?.status && [400, 401, 403, 404].includes(error.response.status)) {
//           throw error;
//         }

//         // Don't retry on the last attempt
//         if (i === maxRetries) {
//           break;
//         }

//         // Wait before retrying
//         await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
//       }
//     }

//     throw lastError;
//   }

//   // Health check
//   async healthCheck(): Promise<boolean> {
//     try {
//       await this.get('/api/health');
//       return true;
//     } catch {
//       return false;
//     }
//   }

//   // Cancel all pending requests
//   cancelAllRequests() {
//     // This would require implementing a request cancellation system
//     // For now, we'll just log that cancellation was requested
//     console.log('Cancelling all pending requests...');
//   }
// }

// // Create and export singleton instance
// export const apiClient = new ApiClient();

// // Export types for use in components
// export type { ApiError, ApiResponse };